import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Select, Space, Button, Table, Pagination, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { getMyBatchList } from '../../../services/debt';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../../config/theme';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

const modeLabel = (m) => {
  if (m === 'full') return '全量新增';
  if (m === 'incremental') return '增量仅新增';
  if (m === 'incremental_full_update') return '增量全更新';
  return String(m || '');
};
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
const statusLabel = (s) => {
  if (s === 'RUNNING') return '进行中';
  if (s === 'COMPLETED') return '已完成';
  if (s === 'FAILED') return '失败';
  return String(s || '');
};
const statusColor = (s) => {
  if (s === 'RUNNING') return 'blue';
  if (s === 'COMPLETED') return 'green';
  if (s === 'FAILED') return 'red';
  return 'default';
};

const BatchList = ({ refreshToken, onOpenReport }) => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const columns = useMemo(() => [
    { title: '批次ID', dataIndex: 'batchId', key: 'batchId', width: 120 },
    { title: '模式', dataIndex: 'mode', key: 'mode', width: 140, render: (v) => modeLabel(v) },
    { title: '总行数', dataIndex: 'totalRows', key: 'totalRows', width: 100, align: 'right', className: 'u-tabular-nums' },
    { title: '已导入', dataIndex: 'importedRows', key: 'importedRows', width: 100, align: 'right', className: 'u-tabular-nums' },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => <Tag color={statusColor(v)}>{statusLabel(v)}</Tag> },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '操作', key: 'action', fixed: 'right', width: 120, render: (_, record) => (
      <Space>
        <Button type="link" onClick={() => onOpenReport && onOpenReport(record)}>查看报表</Button>
      </Space>
    ) },
  ], [onOpenReport]);

  const fetchList = async (pageCfg = pagination) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        status: values.status && values.status !== 'all' ? values.status : undefined,
        mode: values.mode && values.mode !== 'all' ? values.mode : undefined,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getMyBatchList(params);
      const recs = Array.isArray(data?.records) ? data.records : [];
      setRecords(recs);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.page || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载批次列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ current: 1, pageSize: pagination.pageSize, total: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // 检测到列表存在 RUNNING 状态时，每 3 秒刷新一次列表
  useEffect(() => {
    const hasRunning = records.some(r => r.status === 'RUNNING');
    if (!hasRunning) return;
    const timer = setInterval(() => {
      fetchList({ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total });
    }, 3000);
    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, pagination.current, pagination.pageSize, pagination.total]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize, total: 0 });
  };
  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize, total: 0 });
  };
  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    const next = { current: nextCurrent, pageSize: nextPageSize, total: pagination.total };
    setPagination(next);
    fetchList(next);
  };
  return (
    <Card title="我的批次" className="card-compact">
      <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
        <Form.Item name="mode" label="模式" initialValue="all">
          <Select style={{ width: 170 }} options={[
            { label: '全部', value: 'all' },
            { label: '全量新增', value: 'full' },
            { label: '增量仅新增', value: 'incremental' },
            { label: '增量全更新', value: 'incremental_full_update' },
          ]} />
        </Form.Item>
        <Form.Item name="status" label="状态" initialValue="all">
          <Select style={{ width: 150 }} options={[
            { label: '全部', value: 'all' },
            { label: '进行中', value: 'RUNNING' },
            { label: '已完成', value: 'COMPLETED' },
            { label: '失败', value: 'FAILED' },
          ]} />
        </Form.Item>
        <Form.Item>
          <Space wrap>
            <Button type="primary" htmlType="submit">查询</Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>
      <Table
        style={{ marginTop: 12 }}
        rowKey="batchId"
        loading={loading}
        dataSource={records}
        pagination={false}
        columns={columns}
        size={isMobile ? 'small' : 'middle'}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
      />
      <div className="c-pagination-bar">
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          simple={isMobile}
          showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
          pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
          onChange={handlePaginationChange}
        />
      </div>
    </Card>
  );
};

export default BatchList;
